import { useEffect, useState } from 'react';

export const NO_INTERNET_MSG = 'No internet connection. Please check your internet and try again.';

// The device itself is online but our server didn't answer (asleep, slow, blocked)
export const SERVER_MSG = 'We couldn’t reach our server. Please try again in a moment, or message us on WhatsApp.';

// How long to wait for the server. Kept long: after being idle the server can
// take 20+ seconds to wake up, and that is not an internet problem.
export const REQUEST_TIMEOUT_MS = 30000;

// Wake the server up as soon as the page opens, while the person types.
// Any answer (even "not found") wakes it; the answer itself is ignored.
export const wakeServer = () => {
  try {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ping?t=${Date.now()}`, { mode: 'no-cors', cache: 'no-store' }).catch(() => {});
  } catch {
    /* nothing to do */
  }
};

// Short reason shown under the message, so a screenshot tells us what happened
export const reasonOf = (err) => {
  const t = `${err?.code || ''} ${err?.name || ''} ${err?.message || ''}`.toLowerCase();
  if (t.includes('timeout') || t.includes('abort')) return 'timeout';
  return 'network';
};

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Run a request; if it gets NO answer at all (server waking up / hiccup), try once more.
// `noAnswer(err)` tells whether the error had no server answer.
export const withRetry = async (send, noAnswer) => {
  try {
    return await send();
  } catch (err) {
    if (!noAnswer(err) || !navigator.onLine) throw err;
    await wait(2000);
    return send();
  }
};

// Live online/offline status (updates as soon as the connection drops or comes back)
const useOnlineStatus = () => {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
};

export default useOnlineStatus;
